import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { selfHireService } from "@/app/services/selfHireService";
import { useParams } from "react-router-dom";
import {
  crefazFinalSchema,
  crefazStepSchema,
  finalSchema,
  initialSchema,
} from "./components/schema";
import { useSelfHiringContext } from "@/app/contexts/SelfHiringContext";
import { useEffect, useState } from "react";
import { PartnerType } from "@/app/utils/partners.enum";
import { useLeadMutations } from "@/app/hooks/useLeadMutations";
export type CreditPartner = Exclude<PartnerType, "SALVCAR" | "CREFISA" | "C6">;

export const useCreditController = () => {
  const { linkName } = useParams();
  const { step, type, setSeller, selfHireLinkId, setSelfHireLinkId } =
    useSelfHiringContext();

  const [isAuthorizationOpen, setIsAuthorizationOpen] = useState(false);
  const [isErrorOpen, setIsErrorOpen] = useState(false);

  const { updateLead, isUpdatingLead } = useLeadMutations();

  const stepsSchema = {
    1: initialSchema,
    2: type === PartnerType.CREFAZ ? crefazStepSchema : undefined,
    3: type === PartnerType.CREFAZ ? crefazFinalSchema : finalSchema,
  };

  const currentSchema = stepsSchema[step as keyof typeof stepsSchema] as
    | z.ZodObject<any>
    | undefined;
  const activeSchema = currentSchema ?? z.object({});

  type CurrentSchema = z.infer<typeof activeSchema>;
  const methods = useForm<CurrentSchema>({
    resolver: zodResolver(activeSchema),
  });

  const { data: seller } = useQuery({
    queryKey: ["seller-link", linkName],
    queryFn: async () =>
      linkName ? selfHireService.getPublicUser(linkName) : null,
    enabled: !!linkName,
  });

  const { data: partners } = useQuery({
    queryKey: ["link-partners", linkName],
    queryFn: () => selfHireService.getPartnersByLinkName(linkName || ""),
    enabled: !!linkName,
  });

  const { mutate: increaseEntryCount } = useMutation({
    mutationFn: selfHireService.increaseEntryCount,
    onSuccess: (data) => {
      setSelfHireLinkId(data.selfHiringLink._id);
    },
    onError: (error) => {
      console.error("Erro ao aumentar contador de entrada:", error);
    },
  });

  useEffect(() => {
    if (seller) {
      setSeller(seller);
    }
  }, [seller]);

  useEffect(() => {
    if (linkName) {
      const timer = setTimeout(async () => {
        increaseEntryCount({ name: linkName });
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [linkName]);

  return {
    partners,
    methods,
    linkName,
    isErrorOpen,
    setIsErrorOpen,
    isAuthorizationOpen,
    setIsAuthorizationOpen,
    hasCrefaz: partners?.includes(PartnerType.CREFAZ),
    updateLead,
    isUpdatingLead,
    selfHireLinkId,
  };
};
